import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { Icon } from "../icon";
import { RequiredMark } from "../required-mark";
import type { FormInputProps } from "./form-input.types";

/**
 * FormInput Component
 *
 * V&A Waterfront Design System - Form Input
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=10742-20389
 *
 * A single-line text input with label, helper text, and error states.
 *
 * @example
 * // Basic usage
 * <FormInput label="Email" placeholder="Enter your email" />
 *
 * // With error
 * <FormInput label="Email" errorText="Invalid email address" />
 *
 * // On dark background
 * <FormInput label="Email" onDark />
 */
const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>((t0, ref) => {
  const $ = _c(48);
  let className;
  let disabled;
  let errorText;
  let helperText;
  let id;
  let label;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      helperText,
      errorText,
      onDark: t1,
      className,
      disabled,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = errorText;
    $[4] = helperText;
    $[5] = id;
    $[6] = label;
    $[7] = props;
    $[8] = t1;
  } else {
    className = $[1];
    disabled = $[2];
    errorText = $[3];
    helperText = $[4];
    id = $[5];
    label = $[6];
    props = $[7];
    t1 = $[8];
  }
  const onDark = t1 === undefined ? false : t1;
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descriptionId = `${inputId}-description`;
  const hasError = Boolean(errorText);
  const hasDescription = Boolean(errorText || helperText);
  const t2 = onDark && "dark";
  let t3;
  if ($[9] !== className || $[10] !== t2) {
    t3 = cn("group/input flex w-full flex-col gap-2", t2, className);
    $[9] = className;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = disabled ? "text-input-text-disabled" : "text-input-text-label";
  let t5;
  if ($[12] !== t4) {
    t5 = cn("typography-component-sm-medium desktop:typography-component-md-medium", t4);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props.required) {
    t6 = <RequiredMark required={props.required} />;
    $[14] = props.required;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== inputId || $[17] !== label || $[18] !== t5 || $[19] !== t6) {
    t7 = <label htmlFor={inputId} className={t5}>{label}{t6}</label>;
    $[16] = inputId;
    $[17] = label;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = hasError || undefined;
  const t9 = hasDescription ? descriptionId : undefined;
  const t10 = hasError && "bg-input-surface-error hover:bg-input-surface-error focus:bg-input-surface-error pr-12";
  const t11 = disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled";
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = cn("h-12 w-full rounded-sm border border-transparent px-4 py-3", "typography-body-sm-regular", "text-input-text-rest placeholder:text-input-text-disabled", "bg-input-surface-rest", "outline-none", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover [.dark_&]:focus:border-transparent", t10, t11);
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== disabled || $[25] !== inputId || $[26] !== props || $[27] !== ref || $[28] !== t12 || $[29] !== t8 || $[30] !== t9) {
    t13 = <input ref={ref} id={inputId} disabled={disabled} aria-invalid={t8} aria-describedby={t9} className={t12} {...props} />;
    $[24] = disabled;
    $[25] = inputId;
    $[26] = props;
    $[27] = ref;
    $[28] = t12;
    $[29] = t8;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== hasError) {
    t14 = hasError && <Icon name="error" filled={true} size="lg" decorative={true} className="absolute right-4 top-1/2 -translate-y-1/2 text-input-text-error" />;
    $[32] = hasError;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = <div className="relative">{t13}{t14}</div>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== descriptionId || $[38] !== errorText || $[39] !== hasDescription || $[40] !== hasError || $[41] !== helperText) {
    t16 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{errorText || helperText}</p>;
    $[37] = descriptionId;
    $[38] = errorText;
    $[39] = hasDescription;
    $[40] = hasError;
    $[41] = helperText;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t3 || $[46] !== t7) {
    t17 = <div data-testid="form-input" className={t3}>{t7}{t15}{t16}</div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t3;
    $[46] = t7;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
});
FormInput.displayName = "FormInput";
export { FormInput };